// localStorage-based watch data management for Fibernet TV
const KEYS = {
  FAVORITES: 'fibernet_favorites',
  HISTORY: 'fibernet_watch_history',
  PROGRESS: 'fibernet_watch_progress',
  CHANNEL_STATS: 'fibernet_channel_stats',
};

// TTL constants (milliseconds)
const TTL_PROGRESS = 30 * 24 * 60 * 60 * 1000; // 30 days
const TTL_HISTORY = 90 * 24 * 60 * 60 * 1000;  // 90 days
const TTL_CHANNEL_STATS = 90 * 24 * 60 * 60 * 1000; // 90 days
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const MAX_FAVORITES = 50;
const MAX_HISTORY = 30;
const MAX_PROGRESS = 20;
const MAX_CHANNEL_STATS = 100;

// ─── Cleanup ───
function cleanupExpired() {
  const now = Date.now();

  // Cleanup history
  try {
    const history = JSON.parse(localStorage.getItem(KEYS.HISTORY)) || [];
    const fresh = history.filter((h) => now - h.watchedAt < TTL_HISTORY);
    if (fresh.length !== history.length) {
      localStorage.setItem(KEYS.HISTORY, JSON.stringify(fresh));
    }
  } catch { /* ignore */ }

  // Cleanup progress
  try {
    const all = JSON.parse(localStorage.getItem(KEYS.PROGRESS)) || {};
    let changed = false;
    for (const id of Object.keys(all)) {
      if (now - all[id].updatedAt > TTL_PROGRESS) {
        delete all[id];
        changed = true;
      }
    }
    if (changed) localStorage.setItem(KEYS.PROGRESS, JSON.stringify(all));
  } catch { /* ignore */ }

  // Cleanup channel stats (TTL)
  try {
    const stats = JSON.parse(localStorage.getItem(KEYS.CHANNEL_STATS)) || {};
    let changed = false;
    for (const id of Object.keys(stats)) {
      if (now - stats[id].lastWatched > TTL_CHANNEL_STATS) {
        delete stats[id];
        changed = true;
      }
    }
    if (changed) localStorage.setItem(KEYS.CHANNEL_STATS, JSON.stringify(stats));
  } catch { /* ignore */ }
}

// Run cleanup on load
cleanupExpired();

// ─── Favorites (max 50, no TTL) ───
export function getFavorites() {
  try {
    return JSON.parse(localStorage.getItem(KEYS.FAVORITES)) || [];
  } catch { return []; }
}

export function isFavorite(id) {
  return getFavorites().some((f) => f.id === id);
}

export function toggleFavorite(item) {
  const favs = getFavorites();
  const idx = favs.findIndex((f) => f.id === item.id);
  if (idx >= 0) {
    favs.splice(idx, 1);
  } else {
    favs.unshift({
      id: item.id,
      name: item.name,
      poster: item.poster,
      type: item.type,
      genre: item.genre,
      rating: item.rating,
      year: item.year,
      addedAt: Date.now(),
    });
  }
  localStorage.setItem(KEYS.FAVORITES, JSON.stringify(favs.slice(0, MAX_FAVORITES)));
  window.dispatchEvent(new Event('fibernet-favorites-changed'));
  return idx < 0;
}

// ─── Watch History (max 30, 90-day TTL) ───
export function getWatchHistory() {
  try {
    const now = Date.now();
    return (JSON.parse(localStorage.getItem(KEYS.HISTORY)) || [])
      .filter((h) => now - h.watchedAt < TTL_HISTORY);
  } catch { return []; }
}

export function addToHistory(item) {
  const history = getWatchHistory().filter((h) => h.id !== item.id);
  history.unshift({
    id: item.id,
    name: item.name,
    poster: item.poster,
    type: item.type,
    genre: item.genre,
    rating: item.rating,
    year: item.year,
    watchedAt: Date.now(),
  });
  localStorage.setItem(KEYS.HISTORY, JSON.stringify(history.slice(0, MAX_HISTORY)));

  // Track channel-specific stats for hybrid sorting
  if (item.type === 'live') {
    try {
      const stats = JSON.parse(localStorage.getItem(KEYS.CHANNEL_STATS)) || {};
      const now = Date.now();

      // TTL cleanup inline
      for (const id of Object.keys(stats)) {
        if (now - stats[id].lastWatched > TTL_CHANNEL_STATS) delete stats[id];
      }

      // Hard limit: evict lowest-scored entry if at capacity
      const cur = stats[item.id] || { count: 0, lastWatched: 0 };
      if (!stats[item.id] && Object.keys(stats).length >= MAX_CHANNEL_STATS) {
        const entries = Object.entries(stats);
        entries.sort((a, b) => {
          const sa = a[1].count * 10 + (now - a[1].lastWatched < SEVEN_DAYS ? 5 : 0);
          const sb = b[1].count * 10 + (now - b[1].lastWatched < SEVEN_DAYS ? 5 : 0);
          return sa - sb;
        });
        delete stats[entries[0][0]];
      }

      stats[item.id] = {
        id: item.id,
        count: cur.count + 1,
        lastWatched: now,
      };
      localStorage.setItem(KEYS.CHANNEL_STATS, JSON.stringify(stats));
    } catch { /* ignore */ }
  }

  window.dispatchEvent(new Event('fibernet-history-changed'));
}

// ─── Channel Stats (for hybrid live TV sorting) ───
export function getChannelStats() {
  try { return JSON.parse(localStorage.getItem(KEYS.CHANNEL_STATS)) || {}; }
  catch { return {}; }
}

// ─── Watch Progress / Continue Watching (max 20, 30-day TTL) ───
export function getWatchProgress() {
  try {
    const now = Date.now();
    const all = JSON.parse(localStorage.getItem(KEYS.PROGRESS)) || {};
    // Filter expired inline
    for (const id of Object.keys(all)) {
      if (now - all[id].updatedAt > TTL_PROGRESS) delete all[id];
    }
    return all;
  } catch { return {}; }
}

export function getProgress(id) {
  return getWatchProgress()[id] || null;
}

export function saveProgress(id, currentTime, duration, item) {
  if (!duration || duration < 30) return;
  const pct = currentTime / duration;
  if (pct < 0.02 || pct > 0.95) {
    removeProgress(id);
    return;
  }
  const all = getWatchProgress();

  // Enforce max limit — remove oldest if at capacity
  const entries = Object.values(all);
  if (!all[id] && entries.length >= MAX_PROGRESS) {
    const oldest = entries.sort((a, b) => a.updatedAt - b.updatedAt)[0];
    if (oldest) delete all[oldest.id];
  }

  all[id] = {
    id,
    name: item.name,
    poster: item.poster,
    type: item.type,
    genre: item.genre,
    currentTime,
    duration,
    percent: Math.round(pct * 100),
    updatedAt: Date.now(),
  };
  localStorage.setItem(KEYS.PROGRESS, JSON.stringify(all));
  window.dispatchEvent(new Event('fibernet-progress-changed'));
}

export function removeProgress(id) {
  const all = getWatchProgress();
  delete all[id];
  localStorage.setItem(KEYS.PROGRESS, JSON.stringify(all));
}

export function getContinueWatching() {
  const all = getWatchProgress();
  return Object.values(all)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_PROGRESS);
}
